import React from 'react';
import styled from 'styled-components';
import { upperFirst, clone, get } from 'lodash';
import { connect } from 'react-redux';
import { selectThematicSelection } from '_Resources/Cards/Actions/selectThematicSelection';
import { useMutation } from 'react-apollo';
import GET_THEMATICS_OVERVIEW_DATA_QUERY from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicsOverviewData';
import ThematicSelectionDropdown from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/_Containers/DropdownsContainer/_Components/ThematicSelectionDropdown';
import { getSelectedThematicsForSettings } from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/utils';
import SAVE_THEMATICS_OVERVIEW_SETTINGS from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/saveThematicsOverviewSettings';
import { withTranslation } from 'react-i18next';
import ThematicsHorizontalChartDropdown from './_Containers/ThematicsHorizontalChartDropdown/ThematicsHorizontalChartDropdown';

const SelectsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 0 3.5em;
  margin-right: 1em;
  flex-wrap: wrap;
`;

const IndicatorsDropdownsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-right: 2em;
  margin-left: 3em;
  & div {
    margin-right: 0.5em;
  }
`;

type DropdownsContainerProps = {
  horizontalIndicatorsConfig: any;
  filtersToDisplay: Array<{ id: string; label: string }>;
  settings: any;
  setSettings: any;
  defaultSettings: any;
  orderByItems: any[];
  dispatchSelectThematicSelection: Function;
  projectId: number;
  filterKeys: any;
  selectionList: any[];
  selectedThematicSelectionOption: any;
  t: Function;
};

const DropdownsContainer = ({
  horizontalIndicatorsConfig,
  filtersToDisplay,
  settings,
  setSettings,
  defaultSettings,
  orderByItems,
  dispatchSelectThematicSelection,
  projectId,
  filterKeys,
  selectionList,
  selectedThematicSelectionOption,
  t,
}: DropdownsContainerProps) => {
  const allThematics = {
    createdBy: {
      firstName: 'system',
      lastName: 'system',
      email: 'support@qemotion.com',
    },
    id: null,
    label: t('button:All thematics'),
    name: t('button:All thematics'),
    projectId,
    thematics: [],
  };

  const [saveThematicsOverviewSettings] = useMutation(SAVE_THEMATICS_OVERVIEW_SETTINGS, {
    variables: {
      projectId,
      thematicSelectionId: selectedThematicSelectionOption?.id || null,
      settings: JSON.stringify(settings),
    },
    update: () => {},
  });

  const onThematicSelectionChange = selectThematicSelectionOption => {
    dispatchSelectThematicSelection(selectThematicSelectionOption);
    const cloneSettings = clone(settings);
    cloneSettings.selectedThematics = getSelectedThematicsForSettings(selectThematicSelectionOption.thematics);
    if (selectThematicSelectionOption.id !== null) {
      cloneSettings.categoryBreakdown.onlySelectedCategories = true;
    }
    if (selectThematicSelectionOption.id === null) {
      cloneSettings.selectedThematics = [];
      cloneSettings.categoryBreakdown.onlySelectedCategories = false;
    }
    saveThematicsOverviewSettings({
      variables: {
        projectId,
        settings: JSON.stringify(cloneSettings),
        thematicSelectionId: selectThematicSelectionOption?.id || null,
      },
      refetchQueries: [
        {
          query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
          variables: {
            ...filterKeys,
            thematicSelectionId: selectThematicSelectionOption?.id || null,
          },
        },
      ],
    });
  };

  return (
    <div>
      <SelectsContainer>
        <ThematicsHorizontalChartDropdown
          id="filterToDisplayContainer"
          dropdownType="selectedFilter"
          label="Filter to display"
          itemToIterate={filtersToDisplay}
          settings={settings}
          setSettings={setSettings}
          defaultSettings={defaultSettings}
        />
        <IndicatorsDropdownsContainer>
          {Object.keys(horizontalIndicatorsConfig).map(indicator => {
            return (
              <ThematicsHorizontalChartDropdown
                id={`${indicator}IndicatorContainer`}
                dropdownType="indicators"
                subCategory={indicator}
                itemToIterate={horizontalIndicatorsConfig[indicator]}
                label={`${upperFirst(indicator)} value`}
                icon={`${indicator.toUpperCase()}_INDICATOR`}
                settings={settings}
                setSettings={setSettings}
                defaultSettings={defaultSettings}
                orderByItems={orderByItems}
              />
            );
          })}
        </IndicatorsDropdownsContainer>
        <ThematicsHorizontalChartDropdown
          id="orderByDropdownContainer"
          dropdownType="orderBy"
          label="Sort by"
          itemToIterate={orderByItems}
          settings={settings}
          setSettings={setSettings}
          defaultSettings={defaultSettings}
        />
        <ThematicSelectionDropdown
          id="thematicSelectionDropdownContainer"
          label="button:Selected categories"
          t={t}
          options={[allThematics, ...selectionList]}
          thematicSelection={selectedThematicSelectionOption}
          setThematicSelection={onThematicSelectionChange}
          benchmark={false}
        />
      </SelectsContainer>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchSelectThematicSelection: thematicSelection => {
    dispatch(selectThematicSelection(thematicSelection));
  },
});

const mapStateToProps = state => ({
  projectId: get(state, ['projects', 'currentProject', 'id']),
  filterKeys: get(state, ['filters', 'filterKeys']),
  selectionList: get(state, ['thematicSelections', 'list']),
  selectedThematicSelectionOption: get(state, ['thematicSelections', 'selectedThematicSelectionOption']),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('button')(DropdownsContainer));
